/** Clientes v2 para marcas, resolução de semelhantes e cache coletivo de enriquecimento. */
import { api, unwrap } from '../lib/apiClient';
import type { LocalPlace } from '../data/localDb';

export type EnhancedPlaceSource = Pick<
  LocalPlace,
  'cid' | 'emails' | 'phones' | 'websites' | 'social_networks' | 'cnpj'
>;

export interface EnhancedPlacePayload {
  cid: string;
  emails?: string[];
  phones?: NonNullable<LocalPlace['phones']>;
  websites?: string[];
  social_networks?: NonNullable<LocalPlace['social_networks']>;
  cnpj?: string;
}

export interface EnhancedListResponse {
  enhanced: true;
  updated: number;
}

export interface LookalikeListResponse {
  lookalikeRevealed: boolean;
  pendingProfiles?: number;
}

/** Remove os campos exclusivos do IndexedDB antes de atravessar o contrato da API v2. */
export function toEnhancedPlacePayload(place: EnhancedPlaceSource): EnhancedPlacePayload {
  return {
    cid: place.cid,
    ...(place.emails !== undefined ? { emails: [...place.emails] } : {}),
    ...(place.phones !== undefined ? { phones: place.phones.map((phone) => ({ ...phone })) } : {}),
    ...(place.websites !== undefined ? { websites: [...place.websites] } : {}),
    ...(place.social_networks !== undefined
      ? { social_networks: place.social_networks.map((network) => ({ ...network })) }
      : {}),
    ...(place.cnpj !== undefined ? { cnpj: place.cnpj } : {}),
  };
}

function mergeStrings(
  current: string[] | undefined,
  incoming: string[] | undefined,
): string[] | undefined {
  if (current === undefined && incoming === undefined) return undefined;
  return [...new Set([...(current ?? []), ...(incoming ?? [])])];
}

function mergeObjects<T extends object>(
  current: T[] | undefined,
  incoming: T[] | undefined,
  identity: keyof T,
): T[] | undefined {
  if (current === undefined && incoming === undefined) return undefined;
  const merged = new Map<string, T>();
  for (const candidate of [...(current ?? []), ...(incoming ?? [])]) {
    const key = String(candidate[identity] ?? '').trim();
    if (key) merged.set(key, { ...candidate });
  }
  return [...merged.values()];
}

/**
 * Consolida o snapshot por CID antes do envio. O store normalmente já tem um registro por CID,
 * mas esta redução mantém o cliente correto diante de cópias históricas e evita depender do
 * backend para deduplicar o próprio payload.
 */
export function toEnhancedPlacePayloads(
  places: readonly EnhancedPlaceSource[],
): EnhancedPlacePayload[] {
  const byCid = new Map<string, EnhancedPlacePayload>();
  for (const source of places) {
    const incoming = toEnhancedPlacePayload(source);
    const current = byCid.get(incoming.cid);
    if (!current) {
      byCid.set(incoming.cid, incoming);
      continue;
    }
    const emails = mergeStrings(current.emails, incoming.emails);
    const websites = mergeStrings(current.websites, incoming.websites);
    const phones = mergeObjects(current.phones, incoming.phones, 'formated');
    const socialNetworks = mergeObjects(
      current.social_networks,
      incoming.social_networks,
      'name',
    );
    const cnpj = current.cnpj ?? incoming.cnpj;
    byCid.set(incoming.cid, {
      cid: incoming.cid,
      ...(emails !== undefined ? { emails } : {}),
      ...(phones !== undefined ? { phones } : {}),
      ...(websites !== undefined ? { websites } : {}),
      ...(socialNetworks !== undefined ? { social_networks: socialNetworks } : {}),
      ...(cnpj ? { cnpj } : {}),
    });
  }
  return [...byCid.values()];
}

export async function postListEnhanced(
  listId: string,
  places: readonly EnhancedPlaceSource[],
): Promise<EnhancedListResponse> {
  return unwrap(
    await api.post<EnhancedListResponse>(`api/v2/lists/${encodeURIComponent(listId)}/enhanced`, {
      places: toEnhancedPlacePayloads(places),
    }),
  );
}

export async function postListLookalike(
  listId: string,
  resolvedThrough?: string,
): Promise<LookalikeListResponse> {
  return unwrap(
    await api.post<LookalikeListResponse>(
      `api/v2/lists/${encodeURIComponent(listId)}/lookalike`,
      { revealed: true, ...(resolvedThrough ? { resolvedThrough } : {}) },
    ),
  );
}

export interface ResolvedLookalikeContact {
  type: 'email' | 'phone';
  value: string;
  mobile?: boolean;
}

export interface ResolvedLookalikeProfile {
  key: string;
  social_network: string;
  username: string;
  contacts: ResolvedLookalikeContact[];
}

export interface LookalikeResolveRequest {
  snapshot?: string;
  cursor?: string;
  limit: number;
}

export interface LookalikeResolvePage {
  snapshot: string | null;
  nextCursor: string | null;
  total: number;
  processed: number;
  done: boolean;
  profiles: ResolvedLookalikeProfile[];
}

function isContact(value: unknown): value is ResolvedLookalikeContact {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const contact = value as Record<string, unknown>;
  return (
    (contact['type'] === 'email' || contact['type'] === 'phone') &&
    typeof contact['value'] === 'string' &&
    contact['value'].trim().length > 0 &&
    (contact['mobile'] === undefined || typeof contact['mobile'] === 'boolean')
  );
}

function parseLookalikeResolvePage(value: unknown): LookalikeResolvePage {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('invalid_lookalike_response');
  }
  const page = value as Record<string, unknown>;
  const nullableString = (candidate: unknown): candidate is string | null =>
    candidate === null || (typeof candidate === 'string' && candidate.length > 0);
  if (
    !nullableString(page['snapshot']) ||
    !nullableString(page['nextCursor']) ||
    typeof page['total'] !== 'number' ||
    !Number.isInteger(page['total']) ||
    Number(page['total']) < 0 ||
    typeof page['processed'] !== 'number' ||
    !Number.isInteger(page['processed']) ||
    Number(page['processed']) < 0 ||
    typeof page['done'] !== 'boolean' ||
    !Array.isArray(page['profiles']) ||
    Number(page['processed']) !== page['profiles'].length
  ) {
    throw new Error('invalid_lookalike_response');
  }

  const profiles = page['profiles'].map((candidate) => {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
      throw new Error('invalid_lookalike_response');
    }
    const profile = candidate as Record<string, unknown>;
    if (
      typeof profile['key'] !== 'string' ||
      !profile['key'].startsWith('profile:') ||
      typeof profile['social_network'] !== 'string' ||
      !profile['social_network'] ||
      typeof profile['username'] !== 'string' ||
      !profile['username'] ||
      !Array.isArray(profile['contacts']) ||
      !profile['contacts'].every(isContact)
    ) {
      throw new Error('invalid_lookalike_response');
    }
    return {
      key: profile['key'],
      social_network: profile['social_network'],
      username: profile['username'],
      contacts: profile['contacts'],
    };
  });

  if ((page['done'] === true) !== (page['nextCursor'] === null)) {
    throw new Error('invalid_lookalike_response');
  }
  return {
    snapshot: page['snapshot'],
    nextCursor: page['nextCursor'],
    total: Number(page['total']),
    processed: Number(page['processed']),
    done: page['done'],
    profiles,
  };
}

/**
 * Cruza perfis sem contato com o acervo global de leads. É consulta: não grava marca nem altera
 * a lista — quem faz isso é `postListLookalike`, cujo recibo o backup consome.
 */
export async function postLookalikeResolve(
  listId: string,
  request: LookalikeResolveRequest,
  signal?: AbortSignal,
): Promise<LookalikeResolvePage> {
  const response = unwrap(
    await api.post<unknown>(
      `api/v2/lists/${encodeURIComponent(listId)}/lookalike/resolve`,
      request,
      { signal },
    ),
  );
  return parseLookalikeResolvePage(response);
}
